import React from 'react';
import { useBilling } from 'src/hooks/useBilling';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { useInsuranceStore } from 'src/store/insurance';

export const CurrentPlans = () => {
  const { subscriptionPlans, isLoading } = useBilling();
  const { plans: allPlans } = useInsuranceStore();
  
  // Calculate total monthly amount based on plan count and estimated pricing
  // Since API doesn't provide price data, we'll use a placeholder calculation
  const totalMonthlyAmount = subscriptionPlans?.length ? subscriptionPlans.length * 45.00 : 0;

  if (isLoading) {
    return (
      <section className="flex flex-col mt-5 rounded-3xl bg-profile-section-bg pb-16 max-l:mt-0">
        <div className="bg-profile-header-bg text-white px-15 max-md:px-10 rounded-t-3xl py-3.5 pb-2 text-[42px] tracking-wide max-md:text-3xl max-md:py-5">
          Current Plans
        </div>
        <div className="flex justify-center items-center py-8">
          <div><AnimatedDots /></div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col mt-5 rounded-3xl bg-profile-section-bg pb-16 max-l:mt-0">
      <div className="bg-profile-header-bg text-white px-15 max-md:px-10 rounded-t-3xl py-3.5 pb-2 text-[42px] tracking-wide max-md:text-3xl max-md:py-5">
        Current Plans
      </div>
      <div className="flex flex-col px-13.5 mt-8 max-md:px-10 max-xs:px-5">
        <p className="text-xl tracking-wide max-w-[80%] max-l:max-w-full">
          This is a list of your current plans and prices along with your bank
          information. If you have any questions concerning billing please feel
          free to contact a plan specialist here.
        </p>
        <hr className="mt-8.5" />
        <div className="flex justify-between mt-11 max-4xl:flex-col max-4xl:gap-10">
          <div className="flex flex-col max-w-[50%] gap-2 max-4xl:w-1/2 w-full max-md:max-w-full">
            {subscriptionPlans && subscriptionPlans.length > 0 ? (
              subscriptionPlans.map((plan) => (
                <span
                  key={plan.id}
                  className="flex text-2xl justify-between font-bold !font-secondary text-profile-text-muted tracking-wider max-sm:text-xl"
                >
                  {(() => {
                    const matchedPlan = allPlans?.find((p) => p.id === plan.planId);
                    const planName = matchedPlan?.name || matchedPlan?.marketingName || `Plan ${plan.planId}`;
                    const matchedPrice = matchedPlan?.prices?.find((pr) => pr.id === plan.priceId);
                    const priceNumber = matchedPrice?.price ?? matchedPlan?.averagePrice;
                    const priceText = typeof priceNumber === 'number' ? `$${priceNumber}` : '';
                    return (
                      <>
                        <span className="truncate mr-3">{planName}</span>
                        <span className="text-black mr-3">{priceText}</span>
                        <span className="!font-secondary text-black">
                          Status: {plan.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </>
                    );
                  })()}
                </span>
              ))
            ) : (
              <span className="text-2xl font-bold !font-secondary text-profile-text-muted tracking-wider">
                No active plans
              </span>
            )}
          </div>
          <div className="flex flex-col w-[38%] mt-1.5 gap-6 max-xl:gap-4 max-md:w-full max-md:gap-2">
            <p className="uppercase font-bold text-2xl max-md:text-xl ml-2 max-4xl:ml-0 tracking-wider max-sm:text-lg">
              Total Premium:
            </p>
            <span className="ml-3 max-4xl:ml-0 text-7xl max-md:text-5xl font-bold tracking-wide text-primary max-xl:text-6xl max-m:text-5xl max-sm:text-4xl">
              ${totalMonthlyAmount.toFixed(2)}/mo
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
